// Slice 40 — Layout A sidebar (240 px). Header with nested-V logo + STAFF
// chip; primary "Issue license" CTA with ⌘N kbd-chip; two nav groups
// (Workspace / Compliance); footer with avatar + username + sign-out.

import { ROUTE_IDS, type RouteId, useRouter } from "../router/router";
import { HqAvatar, HqIconButton, HqKbd, Icons, Logo, StaffBadge } from "../ui";
import type { IconComponent } from "../ui";
import { useShell } from "./HqShellContext";

interface NavEntry {
  id: RouteId;
  label: string;
  icon: IconComponent;
  group: "Workspace" | "Compliance";
}

const NAV: NavEntry[] = [
  { id: "overview", label: "Overview", icon: Icons.Activity, group: "Workspace" },
  { id: "licenses", label: "Licenses", icon: Icons.Key, group: "Workspace" },
  { id: "feedback", label: "Feedback", icon: Icons.Inbox, group: "Workspace" },
  { id: "customers", label: "Customers", icon: Icons.Users, group: "Workspace" },
  { id: "telemetry", label: "Telemetry", icon: Icons.Telemetry, group: "Workspace" },
  { id: "privacy", label: "Privacy", icon: Icons.Gavel, group: "Compliance" },
  { id: "audit", label: "Audit", icon: Icons.Audit, group: "Compliance" },
  { id: "staff", label: "Staff", icon: Icons.Shield, group: "Compliance" },
  { id: "settings", label: "Settings", icon: Icons.Settings, group: "Compliance" },
];

// Sanity guard: every NAV entry must point at a real route id.
const _routeSet = new Set<string>(ROUTE_IDS);
for (const n of NAV) {
  if (!_routeSet.has(n.id)) throw new Error(`Sidebar NAV references unknown route "${n.id}"`);
}

export function Sidebar() {
  const { route, navigate } = useRouter();
  const { username, role, openIssueKey, signOut } = useShell();
  const workspace = NAV.filter((n) => n.group === "Workspace");
  const compliance = NAV.filter((n) => n.group === "Compliance");
  return (
    <aside
      style={{
        background: "var(--hq-bg-staff)",
        borderRight: "1px solid var(--hq-border)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Header />
      <IssueCta onClick={openIssueKey} />
      <nav className="hq-scroll" style={{ padding: "8px 8px", flex: 1, overflow: "auto" }}>
        <NavGroup label="Workspace">
          {workspace.map((n) => (
            <NavItem key={n.id} entry={n} active={n.id === route} onClick={() => navigate(n.id)} />
          ))}
        </NavGroup>
        <NavGroup label="Compliance">
          {compliance.map((n) => (
            <NavItem key={n.id} entry={n} active={n.id === route} onClick={() => navigate(n.id)} />
          ))}
        </NavGroup>
      </nav>
      <Footer username={username} role={role} signOut={signOut} />
    </aside>
  );
}

function Header() {
  return (
    <div
      style={{
        padding: "16px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        borderBottom: "1px solid var(--hq-border)",
      }}
    >
      <Logo size={22} />
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
        <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "-.01em" }}>Vyvern</span>
        <span
          className="hq-mono"
          style={{
            fontSize: 9.5,
            color: "var(--hq-text-dim)",
            letterSpacing: ".18em",
            textTransform: "uppercase",
          }}
        >
          back-office
        </span>
      </div>
      <span style={{ flex: 1 }} />
      <StaffBadge size="sm" />
    </div>
  );
}

function IssueCta({ onClick }: { onClick: () => void }) {
  return (
    <div style={{ padding: "10px 10px 4px" }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          width: "100%",
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "var(--hq-accent)",
          color: "#04101a",
          border: "none",
          borderRadius: "var(--hq-radius-lg)",
          cursor: "pointer",
          fontWeight: 600,
          fontSize: 12.5,
          boxShadow: "0 0 0 1px rgba(34,211,238,.4), 0 4px 12px rgba(34,211,238,.2)",
          fontFamily: "var(--hq-font-sans)",
        }}
      >
        <Icons.Plus width={14} height={14} sw={2.2} />
        Issue license
        <span style={{ flex: 1 }} />
        <HqKbd>⌘N</HqKbd>
      </button>
    </div>
  );
}

function NavGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        className="hq-mono"
        style={{
          padding: "6px 10px",
          fontSize: 9.5,
          color: "var(--hq-text-faint)",
          letterSpacing: ".15em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>{children}</div>
    </div>
  );
}

function NavItem({
  entry,
  active,
  onClick,
}: {
  entry: NavEntry;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 10px",
        borderRadius: "var(--hq-radius-md)",
        background: active ? "var(--hq-bg-active)" : "transparent",
        border: "none",
        cursor: "pointer",
        color: active ? "var(--hq-text)" : "var(--hq-text-muted)",
        fontSize: 12.5,
        fontWeight: active ? 500 : 400,
        textAlign: "left",
        width: "100%",
        transition: "background .12s, color .12s",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = "var(--hq-bg-hover)";
          e.currentTarget.style.color = "var(--hq-text)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = "var(--hq-text-muted)";
        }
      }}
    >
      <entry.icon
        width={14}
        height={14}
        sw={1.7}
        style={{ color: active ? "var(--hq-accent)" : "currentColor" }}
      />
      <span style={{ flex: 1 }}>{entry.label}</span>
    </button>
  );
}

function Footer({
  username,
  role,
  signOut,
}: {
  username: string;
  role: "admin" | "staff";
  signOut: () => void;
}) {
  return (
    <div
      className="hq-staff-stripe-bg"
      style={{
        padding: "12px 14px",
        borderTop: "1px solid var(--hq-border)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <HqAvatar name={username} size={26} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "var(--hq-text)", fontWeight: 500 }}>{username}</div>
        <div
          className="hq-mono"
          style={{
            fontSize: 10,
            color: "var(--hq-text-dim)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {role === "admin" ? "admin" : "staff (read-only)"}
        </div>
      </div>
      <HqIconButton icon={Icons.Logout} size="sm" title="Sign out" onClick={signOut} />
    </div>
  );
}
